import { SxProps } from "@mui/material";
import Dialog, { DialogProps } from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { isNullOrUndefined } from "csi/utility/coreUtility";
import { useResizeObserver } from "designSystem/hooks";
import { grey } from "designSystem/styles/color";
import { stopMuiEvent } from "designSystem/utilities/prevent-default-behavior";
import React, { forwardRef, FunctionComponent, ReactElement, ReactNode, SyntheticEvent } from "react";
import Box, { BoxProps } from "../box/Box";
import FlexColumn from "../flex/FlexColumn";
import FlexSideDrawer, { FlexSideDrawerProps } from "../flex/FlexSideDrawer";
import DialogTitle from "./DialogTitle";

// padding for dialogs
//	- if not showing dividers, then padding is generally 12px with reduced padding between content and title/actions
//	- if showing dividers, then left/right padding is now 16px, although title/actions retain 12px right padding since
//		close and cancel buttons do not show full width until hover

const TITLE_DIVIDER_STYLES = {
	backgroundColor: grey[50],
	borderRadius: "3px",
	paddingLeft: 4,
};

function _dialogStyles(alignToTop: boolean, zIndex: number | undefined, sx: SxProps): SxProps {
	let xStyles = alignToTop ?
		{
			"& .MuiDialog-container": {
				alignItems: "flex-start",
			},
			...sx,
		}
		:
		sx;

	if (!!zIndex) {
		xStyles = {
			zIndex,
			...xStyles,
		};
	}

	return xStyles;
}

export type ModalDialogDrawerProps = Omit<FlexSideDrawerProps, "children" | "rightDrawer">;
export type ContentScrollOptions = "all" | "none" | "belowMessage";

export interface ModalDialogProps extends Omit<DialogProps, "disableBackdropClick" | "onClose" | "PaperProps" | "children" | "onResize"> {
	/** align dialog to top of screen rather than be centered */
	alignToTop?: boolean;

	/** child components of dialog (leaving possibility for extra content like checkboxes, etc.) */
	children?: {
		/** actions for the dialog */
		buttons?: ReactNode;

		/** the dialog header */
		header?: ReactNode;

		/** the main content of the dialog */
		main?: ReactNode;

		/** message to add on top of the main content */
		message?: ReactNode;
	};

	/** css class name */
	className?: string;

	/** add "display:flex" to content container */
	contentDisplayFlex?: boolean;

	/** the spacing around the content or the message if any, the default number is 4 */
	contentSpacing?: number;

	/** data-csi-automation for labeling */
	dataCsiAutomation?: string;

	/** 
	 * the behavior of how the content should scroll, "all" by default
	 * all - the whole content, including the content and the message if any
	 * none - the whole content can't be able to scroll
	 * belowMessage - only the content can be scrolled, the message part would stay at the top if any, note: we can still use this option even though no message at all, it would make the height of content container equals 100%
	 */
	scrollContent?: ContentScrollOptions;

	/** indicates if dialog should expand to fill full height of viewport (minus min margin) */
	fullHeight?: boolean;

	/** indicates if dialog should expand to fill full width of viewport (minus min margin) */
	fullWidth?: boolean;

	/** indicates if dialog should be hidden from view (for example, until content is loaded) */
	hide?: boolean;

	/** properties to use for left drawer */
	leftDrawerProps?: ModalDialogDrawerProps;

	/** minimum width of dialog */
	minWidth?: number | string;

	/** event handler called by when dialog closes */
	onClose?: VoidFunction;

	/** event handler called by when dialog resize */
	onResize?: (width: number, height: number) => void;

	/** properties to use for right drawer */
	rightDrawerProps?: ModalDialogDrawerProps;

	/** indicates if close button should be shown in header (default is false) */
	showClose?: boolean;

	/** indicates if dividers should be shown between header, content, and buttons (default is false) */
	showDividers?: boolean;

	/** dialog title (if not specifying localization resource) */
	title?: string;

	/** localization resource for dialog title */
	titleRes?: string;

	/** parameters for localization resource for dialog title */
	titleResParams?: Array<number | string>;

	/** width of dialog */
	width?: number | string;

	/** z index style to use for dialog */
	zIndex?: number;
}

interface ModalDialogDrawerContentProps extends Pick<ModalDialogProps, "leftDrawerProps" | "rightDrawerProps"> {
	children: ReactElement;
}

const ModalDialogDrawerContent: FunctionComponent<ModalDialogDrawerContentProps> = ({ children, leftDrawerProps, rightDrawerProps }) => {
	if (leftDrawerProps && rightDrawerProps) {
		return (
			<ModalDialogDrawerContent leftDrawerProps={leftDrawerProps}>
				<FlexSideDrawer
					rightDrawer
					{...rightDrawerProps}
				>
					{children}
				</FlexSideDrawer>
			</ModalDialogDrawerContent>
		);
	}

	const drawerProps = leftDrawerProps || rightDrawerProps;
	if (drawerProps) {
		return (
			<FlexSideDrawer
				rightDrawer={!!rightDrawerProps}
				{...drawerProps}
			>
				{children}
			</FlexSideDrawer>
		);
	}

	return children;
};

const ModalDialog: FunctionComponent<ModalDialogProps> = forwardRef((props, ref) => {
	const {
		alignToTop,
		children,
		className,
		contentDisplayFlex,
		contentSpacing,
		dataCsiAutomation,
		fullHeight,
		fullWidth,
		hide,
		leftDrawerProps,
		minWidth,
		onClose,
		onResize,
		rightDrawerProps,
		scrollContent = "all",
		showClose,
		showDividers,
		sx,
		title,
		titleRes,
		titleResParams,
		width,
		zIndex,
		...otherProps
	} = props;

	const titleId = "modal-dialog-title";
	const titleAutomation = dataCsiAutomation ? `modal-Title-${dataCsiAutomation}` : undefined;

	const hasMessage = children?.message;
	const isBelowMessage = scrollContent === "belowMessage";
	const overflowForContent = scrollContent === "none" ? "hidden" : (isBelowMessage ? "auto" : null);
	const overflowForContentContainer = scrollContent === "all" ? "auto" : "hidden";
	const additionPropsForContent: Partial<BoxProps> = {};
	const actualContentSpacing = isNullOrUndefined(contentSpacing) ? (showDividers ? 4 : "8px 12px") : contentSpacing;

	if (isBelowMessage) {
		additionPropsForContent.height = 1;
	}

	if (contentDisplayFlex) {
		additionPropsForContent.display = "flex";
	}

	const _onClose = (event: SyntheticEvent, reason: "backdropClick" | "escapeKeyDown") => {
		if (reason !== "backdropClick") {
			onClose?.();
		} else {
			stopMuiEvent(event);
		}
	};

	const { ref: resizeRef } = useResizeObserver<HTMLDivElement>({
		onResize: ({ width, height }) => {
			onResize?.(width, height);
		},
	});

	return (
		<Dialog
			PaperProps={{
				sx: {
					height: fullHeight ? "100%" : null,
					minWidth,
					visibility: hide ? "hidden" : null,
					width: fullWidth ? "100%" : width,
				},
			}}
			aria-labelledby={titleId}
			onClose={_onClose}
			ref={ref}
			sx={_dialogStyles(alignToTop, zIndex, sx)}
			{...otherProps}
		>
			{children?.header || (
				<DialogTitle
					data-csi-automation={titleAutomation}
					id={titleId}
					onClose={showClose ? onClose : null}
					res={titleRes}
					resParams={titleResParams}
					sx={showDividers ? TITLE_DIVIDER_STYLES : null}
					text={title}
				/>
			)}
			<DialogContent
				className={className}
				dividers={showDividers || false}
				ref={resizeRef}
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "100%",
					padding: 0,
					width: "100%",
				}}
			>
				<ModalDialogDrawerContent
					leftDrawerProps={leftDrawerProps}
					rightDrawerProps={rightDrawerProps}
				>
					<FlexColumn overflow={overflowForContentContainer}>
						{hasMessage && (
							<Box padding={actualContentSpacing}>
								{children.message}
							</Box>
						)}
						<Box
							flexGrow={1}
							overflow={overflowForContent}
							padding={actualContentSpacing}
							paddingTop={hasMessage ? 0 : actualContentSpacing}
							{...additionPropsForContent}
						>
							{children?.main}
						</Box>
					</FlexColumn>
				</ModalDialogDrawerContent>
			</DialogContent>
			{children?.buttons && (
				<DialogActions>
					{children.buttons}
				</DialogActions>
			)}
		</Dialog>
	);
});

export default ModalDialog;